import React, { useState } from 'react';
import {
    Box, Button, Typography, IconButton, Tooltip, Stack,
    Chip,
    Paper,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileUploadPopup from '../popup/file-upload-popup';

import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircle from "@mui/icons-material/CheckCircle";

export interface StorefrontImageMeta {
    url: string;
    file_name: string;
    file_type: string;
    file_path: string;
    storage_size: number | string;
}

interface Props {
    label?: string;
    value: string;
    uploadPath: string;
    hint?: string;
    previewVariant?: "logo" | "banner";
    onChange: (meta: StorefrontImageMeta) => void;
    onRemove: () => void;

    error?: boolean;
    helperText?: string;
}

// Logo / banner uploader for the storefront — reuses the platform-wide FileUploadPopup
// (POST /upload) and surfaces the same metadata other features store (url, key, fileName,
// fileType, storage_size).
export const StorefrontImageUpload: React.FC<Props> = ({ error, label, value, uploadPath, hint, previewVariant = 'logo', onChange, onRemove, helperText }) => {
    const [open, setOpen] = useState(false);

    const isBanner = previewVariant === 'banner';
    const previewSx = isBanner
        ? { width: '100%', height: 120, borderRadius: 1 }
        : { width: 80, height: 80, borderRadius: 1 };

    return (
        <Box sx={{ width: "100%" }}>

            {/* Label */}

            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
            >
                <Typography
                    fontWeight={700}
                    fontSize={17}
                >
                    {label}
                </Typography>

                <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={
                        previewVariant === "banner"
                            ? "1600 × 500 Recommended"
                            : "500 × 500 Recommended"
                    }
                />
            </Box>

            <Paper
                elevation={0}
                onClick={!value ? () => setOpen(true) : undefined}
                sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    border: "2px dashed",
                    borderColor: error ? "#ef4444" : "#CBD5E1",
                    transition: "all .25s ease",
                    cursor: !value ? "pointer" : "default",

                    "&:hover": {
                        borderColor: "#6366F1",
                        boxShadow: "0 15px 35px rgba(99,102,241,.12)",
                        transform: "translateY(-2px)",
                    },
                }}
            >
                {/* {hint && (
    <Typography
        variant="caption"
        color="text.secondary"
        sx={{
            display: "block",
            mt: 1,
            textAlign: "center",
        }}
    >
        {hint}
    </Typography>
)} */}
                {value ? (
                    <Box sx={{ position: "relative" }}>
<Chip
                label="Uploaded"
                color="success"
                size="small"
                sx={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                }}
            />
                        {previewVariant === "banner" ? (

                            <img
                                src={value}
                                alt={label}
                                style={{
                                    width: "100%",
                                    height: 260,
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />

                        ) : (

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    bgcolor: "#FAFBFF",
                                    py: 5,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 140,
                                        height: 140,
                                        borderRadius: "50%",
                                        overflow: "hidden",
                                        border: "5px solid white",
                                        boxShadow: "0 10px 30px rgba(0,0,0,.12)",
                                        padding: '8px',
                                    }}
                                >
                                    <img
                                        src={value}
                                        alt={label}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                </Box>
                            </Box>

                        )}

                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove();
                            }}
                            sx={{
                                position: "absolute",
                                top: 16,
                                right: 16,
                                width: 40,
                                height: 40,
                                bgcolor: "rgba(255,255,255,.95)",
                                boxShadow: 2,

                                "&:hover": {
                                    bgcolor: "#fff",
                                    transform: "scale(1.05)",
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>

                        <Box
                            sx={{
                                p: 2,
                                display: "flex",
                                justifyContent: "center",
                                borderTop: "1px solid #eee",
                            }}
                        >
                            {/* <Button
        variant="contained"
        color="primary"
        onClick={(e) => {
        e.stopPropagation();
        setOpen(true);
    }}
        sx={{
            borderRadius: 3,
            textTransform: "none",
            px: 3,
            boxShadow: "none",

            "&:hover": {
                boxShadow: "0 8px 20px rgba(99,102,241,.25)",
            },
        }}
    >
        Replace {label}
    </Button> */}

                            <Button
                                startIcon={<CloudUploadOutlined />}
                                variant="text"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpen(true);
                                }}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                }}
                            >
                                Replace {label}</Button>
                        </Box>

                    </Box>
                ) : (
                    <Box
                        sx={{
                            height: previewVariant === "banner" ? 200 : 200,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            bgcolor: "#FAFBFF",
                            padding: 2,
                            textAlign: 'center'
                        }}
                    >
                        <CloudUploadOutlined
                            sx={{
                                fontSize: 60,
                                color: "#6366F1",
                                mb: 1,
                            }}
                        />

                        <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ lineHeight: 1.2 }}
                        >
                            Upload {label}
                        </Typography>

                        <Typography
                            color="text.secondary"
                            variant="body2"
                            sx={{
                                mt: 0,
                                mb: 1,
                            }}
                        >
                            Drag & Drop or Click to Browse
                        </Typography>

                        <Chip
                            color="primary"
                            variant="outlined"
                            label="PNG • JPG • Max 2 MB"
                        />
                    </Box>
                )}
            </Paper>

            {/* {
        error &&

        <Typography
            color="error"
            mt={1}
            fontSize={13}
        >
            {helperText}
        </Typography>
    } */}
            {helperText && (
                <Typography
                    color="error"
                    variant="caption"
                    sx={{
                        mt: 1,
                        display: "block",
                        textAlign: "center",
                    }}
                >
                    {helperText}
                </Typography>
            )}
            <Box
                sx={{
                    bgcolor: "#fff",
                    borderRadius: 4,
                    p: 2,
                    position:'relative'
                }}
            >
                <FileUploadPopup
                    openModal={open}
                    onClose={() => setOpen(false)}
                    acceptedFileTypes=".jpg, .jpeg, .png"
                    path={uploadPath}
                    coverImage={previewVariant === "banner"}
                    coverImageDimensionText={
                        previewVariant === "banner"
                            ? "Recommended: 1600 × 500"
                            : "Recommended: 500 × 500"
                    }
                    onSend={(file: any) => {
                        onChange({
                            url: file.url,
                            file_name: file.file_name,
                            file_type: file.file_type,
                            file_path: file.file_path,
                            storage_size: file.storage_size,
                        });

                        setOpen(false);
                    }}
                />
            </Box>
            
        </Box>
    );
};
